import React, { useCallback, useMemo, useState } from 'react';
import {
  Card, CardContent, Avatar, Typography, Box,
  Button, Collapse, Stack, Divider, alpha
} from '@mui/material';
import {
  TouchApp as TriggerIcon,
  CheckCircle as SuccessIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Schedule as ClockIcon,
} from '@mui/icons-material';
import './TimelineCard.css';

const typeConfig = {
  trigger: { color: 'primary',   icon: TriggerIcon, main: '#1976d2', light: alpha('#1976d2', 0.1) },
  success: { color: 'success',   icon: SuccessIcon, main: '#2e7d32', light: alpha('#2e7d32', 0.1) },
  info:    { color: 'info',      icon: InfoIcon,    main: '#0288d1', light: alpha('#0288d1', 0.1) },
  warning: { color: 'warning',   icon: WarningIcon, main: '#ed6c02', light: alpha('#ed6c02', 0.1) },
  error:   { color: 'error',     icon: ErrorIcon,   main: '#d32f2f', light: alpha('#d32f2f', 0.1) },
};


const BaseTimelineCard = ({
  item,
  onItemClick,
  isSelected,
  formatDateTime,
  config,
  headerContent,
  children,
  hasDetails
}) => {
  const [expanded, setExpanded] = useState(false);
  const IconComponent = config.icon;

  const formattedDate = useMemo(() => {
    if (!item.datetime) return 'Invalid Date';
    return formatDateTime
      ? formatDateTime(item.datetime)
      : new Date(item.datetime).toLocaleString();
  }, [item.datetime, formatDateTime]);

  const handleClick = useCallback(() => {
    onItemClick && onItemClick(item);
  }, [item, onItemClick]);

  const handleExpand = useCallback((e) => {
    e.stopPropagation();
    setExpanded(prev => !prev);
  }, []);

  return (
    <Card
      onClick={handleClick}
      className={`timeline-card ${isSelected ? 'selected' : ''}`}
      style={{ '--theme-color': config.main }}
    >
      <CardContent className="timeline-card-content">
        <Stack direction="row" spacing={1.5} className="timeline-card-row">
          <Avatar className="timeline-card-avatar" sx={{ bgcolor: config.main }}>
            <IconComponent className="timeline-card-avatar-icon" />
          </Avatar>

          <Box className="timeline-card-main">
            {headerContent}

            <Stack direction="row" spacing={0.5} className="timeline-card-time-row">
              <ClockIcon color="action" className="timeline-card-time-icon" />
              <Typography variant="caption" color="text.secondary" className="timeline-card-time-text">
                {formattedDate}
              </Typography>
            </Stack>

            {hasDetails && (
              <Button
                onClick={handleExpand}
                variant="text"
                size="small"
                className="timeline-card-details-button"
                endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                color="inherit"
              >
                {expanded ? 'Less' : 'Details'}
              </Button>
            )}

            <Collapse in={expanded} timeout="auto" unmountOnExit>
              {children}
            </Collapse>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------
// 1. EVENT CARD
// ---------------------------------------------------------------
export const EventCard = React.memo(({ item, onItemClick, isSelected, formatDateTime }) => {
  const config = typeConfig[item.trigger_type] || typeConfig.info;

  const headerContent = (
    <Box className="timeline-card-header">
      <Typography variant="body2" className="timeline-card-title">
        {item.trigger_type?.charAt(0).toUpperCase() + item.trigger_type?.slice(1) || 'Event'}: {item.summary || 'No summary'}
      </Typography>
    </Box>
  );

  const details = useMemo(() => {
    const hi = item.hover_information || {};
    const additionalData = hi.additional_data || {};
    
    const baseDetails = [
      { label: 'VIN', value: hi.vin },
      { label: 'MAC', value: hi.mac_id },
      { label: 'End Date', value: hi.end_date ? (formatDateTime ? formatDateTime(hi.end_date) : new Date(hi.end_date).toLocaleString()) : null },
    ].filter(d => d.value);

    const additionalDetails = Object.entries(additionalData).map(([key, value]) => ({
      label: key,
      value: typeof value === 'number' ? value.toLocaleString() : value
    }));

    return [...baseDetails, ...additionalDetails];
  }, [item.hover_information, formatDateTime]);

  return (
    <BaseTimelineCard
      item={item}
      onItemClick={onItemClick}
      isSelected={isSelected}
      formatDateTime={formatDateTime}
      config={config}
      headerContent={headerContent}
      hasDetails={details.length > 0}
    >
      <Box className="timeline-card-expanded">
        <Divider className="timeline-card-divider" />
        <Stack className="timeline-card-detail-stack">
          {details.map(({ label, value }) => (
            <Box key={label} className="timeline-card-detail-box">
              <Typography variant="caption" color="text.secondary" className="timeline-card-detail-label">
                {label}
              </Typography>
              <Typography variant="body2" className="timeline-card-detail-value">{value}</Typography>
            </Box>
          ))}
        </Stack>
      </Box>
    </BaseTimelineCard>
  );
});

// ---------------------------------------------------------------
// 2. TEST RESULT CARD
// ---------------------------------------------------------------
export const TestResultCard = React.memo(({ item, onItemClick, isSelected, formatDateTime }) => {
  const config = typeConfig[item.trigger_type] || typeConfig.info;
  const hi = item.hover_information || {};
  const hasFailure = Array.isArray(hi.failureReasons) && hi.failureReasons.length > 0;

  const format = (iso) =>
    formatDateTime ? formatDateTime(iso) : new Date(iso).toLocaleString();

  const failureBoxes = hasFailure
    ? hi.failureReasons.map((fr, idx) => ({
        key: `fr-${idx}`,
        label: fr.reason_type || 'Unknown',
        value: (
          <Box className="timeline-card-failure-value">
            <strong>{fr.data}</strong>
            {fr.datetime && (
              <Typography component="span" color="text.secondary" className="timeline-card-failure-time">
                at {format(fr.datetime)}
              </Typography>
            )}
          </Box>
        )
      }))
    : [];

  const details = [
    { label: 'Source', value: hi.source },
    {
      label: 'Elapsed',
      value: hi.elapsed != null ? `${(hi.elapsed / 60).toFixed(2)} min` : null
    },
    { label: 'Start Time', value: hi.startTime ? format(hi.startTime) : null },
    ...failureBoxes
  ].filter(d => d.value != null && d.value !== '');

  const headerContent = (
    <Box sx={{ position: 'relative' }} className="timeline-card-header">
      <Box>
        <Typography variant="body2" className="timeline-card-title">
          Test: {item.trigger_type || 'Unknown'}
        </Typography>
        <Typography variant="body2" color="text.secondary" className="timeline-card-subtitle">
          {item.summary || 'Unknown Suite'}
        </Typography>
      </Box>

      {hasFailure && (
        <WarningIcon className="timeline-card-warning-icon" />
      )}
    </Box>
  );

  return (
    <BaseTimelineCard
      item={item}
      onItemClick={onItemClick}
      isSelected={isSelected}
      formatDateTime={formatDateTime}
      config={config}
      headerContent={headerContent}
      hasDetails={details.length > 0}
    >
      <Box className="timeline-card-expanded">
        <Divider className="timeline-card-divider" />
        <Stack className="timeline-card-detail-stack">
          {details.map(({ label, value, key }) => (
            <Box key={key || label} className="timeline-card-detail-box">
              <Typography variant="caption" color="text.secondary" className="timeline-card-detail-label">
                {label}
              </Typography>
              <Typography variant="body2" className="timeline-card-detail-value">
                {value}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>
    </BaseTimelineCard>
  );
});

// ---------------------------------------------------------------
// 3. DTC LOG CARD
// ---------------------------------------------------------------
export const DtcLogCard = React.memo(({ item, onItemClick, isSelected, formatDateTime }) => {
  const config = typeConfig[item.trigger_type] || typeConfig.info;

  const headerContent = (
    <Box className="timeline-card-header">
      <Typography variant="body2" className="timeline-card-title">
        DTC: {item.summary || ''}
      </Typography>
      <Typography variant="body2" color="text.secondary" className="timeline-card-mono">
        {item.ecu ? `0x${item.ecu.toString(16).toUpperCase()}` : ''}{' '}
        {item.dtc_code || ''}{' '}
        {item.failure_type_byte ? `(0x${item.failure_type_byte.toString(16).toUpperCase()})` : ''}{' '}
        {item.status_mask ? `0x${item.status_mask.toString(16).toUpperCase()}` : ''}
      </Typography>
    </Box>
  );

  const hi = item.hover_information || {};
  const details = [
    { label: 'ECU', value: hi.ecu },
    { label: 'DTC', value: hi.dtc },
  ].filter(d => d.value);

  return (
    <BaseTimelineCard
      item={item}
      onItemClick={onItemClick}
      isSelected={isSelected}
      formatDateTime={formatDateTime}
      config={config}
      headerContent={headerContent}
      hasDetails={details.length > 0}
    >
      <Box className="timeline-card-expanded">
        <Divider className="timeline-card-divider" />
        <Stack className="timeline-card-detail-stack">
          {details.map(({ label, value }) => (
            <Box key={label} className="timeline-card-detail-box">
              <Typography variant="caption" color="text.secondary" className="timeline-card-detail-label">
                {label}
              </Typography>
              <Typography variant="body2" className="timeline-card-detail-value">{value}</Typography>
            </Box>
          ))}
        </Stack>
      </Box>
    </BaseTimelineCard>
  );
});


export const TimelineCard = React.memo(({ item, onItemClick, isSelected, formatDateTime }) => {
  switch (item.type) {
    case 'test_result':
      return <TestResultCard item={item} onItemClick={onItemClick} isSelected={isSelected} formatDateTime={formatDateTime} />;
    case 'dtc_log':
      return <DtcLogCard item={item} onItemClick={onItemClick} isSelected={isSelected} formatDateTime={formatDateTime} />;
    case 'event':
      return <EventCard item={item} onItemClick={onItemClick} isSelected={isSelected} formatDateTime={formatDateTime} />;
    default:
      return null;
  }
});